import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MailCheck, KeyRound } from "lucide-react";
import { toast } from "sonner";

function Shell({ icon: Icon, title, sub, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
      <div className="bg-card border rounded-xl p-6 w-full max-w-sm shadow-sm fade-in">
        <img src="/omi-logo.webp" alt="OMI Fintech Pvt Ltd" className="h-14 w-auto mb-4" />
        <div className="flex items-center gap-2 mb-1"><Icon className="h-5 w-5 text-primary" /><h1 className="text-xl font-bold">{title}</h1></div>
        <p className="text-sm text-muted-foreground mb-5">{sub}</p>
        {children}
        <Link to="/login" className="block text-sm text-primary mt-5" data-testid="back-to-login-link">Back to sign in</Link>
      </div>
    </div>
  );
}

export function ForgotPassword() {
  const [eid, setEid] = useState("");
  const [sent, setSent] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { const { data } = await api.post("/auth/forgot-password", { employee_id: eid }); setSent(data.message); }
    catch (ex) { toast.error(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell icon={MailCheck} title="Forgot password" sub="Enter your Employee ID. We'll send a reset link to your work email. It works once, for 30 minutes.">
      {sent ? <div className="text-sm rounded-lg bg-emerald-50 text-emerald-800 px-3 py-2" data-testid="forgot-sent-message">{sent} No work email on file? Ask your manager or admin to change your password.</div> : (
        <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
          <div><Label>Employee ID</Label><Input className="mt-1.5 font-mono uppercase" value={eid} onChange={(e) => setEid(e.target.value)} autoFocus data-testid="forgot-employee-id-input" /></div>
          <Button type="submit" className="w-full" disabled={busy || !eid.trim()} data-testid="forgot-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a reset link"}</Button>
        </form>)}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState(""); const [confirm, setConfirm] = useState(""); const [busy, setBusy] = useState(false); const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (pw.length < 8 || !/[A-Za-z]/.test(pw) || !/\d/.test(pw)) return setErr("Password must be at least 8 characters with letters and numbers");
    if (pw !== confirm) return setErr("The two passwords don't match");
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, new_password: pw }); toast.success("Password changed. Please sign in."); nav("/login"); }
    catch (ex) { setErr(apiError(ex.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <Shell icon={KeyRound} title="Choose a new password" sub="At least 8 characters, with letters and numbers.">
      {!token ? <p className="text-sm text-destructive" data-testid="reset-error">This reset link is incomplete. Please ask for a new one.</p> : (
        <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
          <div><Label>New password</Label><Input type="password" className="mt-1.5" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new-input" /></div>
          <div><Label>Repeat new password</Label><Input type="password" className="mt-1.5" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm-input" /></div>
          {err && <div className="text-sm text-destructive bg-destructive/10 rounded-lg px-3 py-2" data-testid="reset-error">{err}{/expired|invalid/i.test(err) && <> <Link to="/forgot-password" className="underline">Ask for a new link</Link></>}</div>}
          <Button type="submit" className="w-full" disabled={busy || !pw} data-testid="reset-submit-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save new password"}</Button>
        </form>)}
    </Shell>
  );
}

export default ForgotPassword;
